import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import { DEFAULT_DAYS, defaultTimezone } from "./DayOfWeekField";
import { SetupPage, When, Step, Schedule, openInBuilder } from "./SetupFlow";

// What each block of the Slack message shows — mirrors the template's message on the server.
const LINES = [
  ["Emails", "sent · replies"],
  ["LinkedIn", "connections sent · accepted · messages sent · replies"],
  ["Calls", "made · connected"]
];

const CreateActivityReport = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [scope, setScope] = useState("me"); // me | team
  const [perPerson, setPerPerson] = useState(false);
  const [runWhen, setRunWhen] = useState("DAILY"); // DAILY | WEEKDAYS | WEEKLY
  const [time, setTime] = useState("09:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [slackChannelId, setSlackChannelId] = useState("");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => {
    agentsData.fetchSlackChannels();
    // Slack is connected in a new tab: pick up the channels when they come back.
    const refresh = () => { if (!document.hidden) agentsData.fetchSlackChannels(); };
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, []);

  const channels = agentsData.slackChannels || [];
  const slackMissing = agentsData.slackChannelsLoaded && !channels.length;
  const channel = channels.find(c => c.id === slackChannelId);
  const splitByPerson = scope === "team" && perPerson;

  const buildPayload = () => ({
    name: name.trim() || (scope === "team" ? "Team activity report" : "Daily activity report"),
    scope,
    perPerson: splitByPerson,
    frequency: runWhen,
    time,
    ...(runWhen === "WEEKLY" ? { days } : {}),
    timezone,
    slackChannelId: slackChannelId || undefined,
    slackChannelName: channel ? channel.name : undefined
  });

  const submit = async () => {
    setSaving(true);
    const agent = await agentsData.createActivityReport(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = () => openInBuilder(history, "activity-report", buildPayload(), setSaving);

  return (
    <SetupPage
      title="Hire · Daily activity report"
      name={{ value: name, onChange: setName, placeholder: scope === "team" ? "Team activity report" : "Daily activity report" }}
      bar={{ canSubmit: !slackMissing, saving, onCreate: submit, onCustomize: customize, onCancel: () => history.push("/agents"), reason: "Connect Slack to create it." }}>
      <When icon="clock">
        <Schedule runWhen={runWhen} setRunWhen={setRunWhen} days={days} setDays={setDays} time={time} setTime={setTime} timezone={timezone} manual={false} />
        <span className="ag-sf__hint">Each report covers the whole of yesterday.</span>
      </When>
      <Step icon="stage" tone="violet" title="Read yesterday's numbers" tags={LINES.map(([k]) => ({ label: k }))}>
        <AgentSelect value={scope} onChange={setScope} options={[
          { value: "me", label: "My activity" },
          { value: "team", label: "My team's activity" }
        ]} />
        {scope === "team" && (
          <label className="ag-sf__check">
            <input type="checkbox" checked={perPerson} onChange={e => setPerPerson(e.target.checked)} />
            <span>Show each person's numbers separately</span>
          </label>
        )}
      </Step>
      <Step icon="send" tone="grey" title="Post to Slack">
        {slackMissing ? (
          <span className="ag-sf__hint is-warn">Slack isn't connected. <a className="ag-link" href={history.createHref({ pathname: "/settings/crm/slack" })} target="_blank" rel="noopener noreferrer">Connect Slack</a>, then come back.</span>
        ) : (
          <AgentSelect value={slackChannelId} searchable onChange={setSlackChannelId}
            options={[{ value: "", label: "Your team's channel" }].concat(channels.map(c => ({
              value: c.id, label: "#" + c.name + (c.teamDefault ? " (team's channel)" : "")
            })))} />
        )}
      </Step>
    </SetupPage>
  );
});

export default CreateActivityReport;
